import { Fragment, ReactNode, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Control,
  FieldErrors,
  Path,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { Button, Card, Col, Collapse, Row, Table, Typography } from 'antd';
import {
  CheckOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import { ErrorText, MessageBox } from '@/components/Elements';
import styles from './factFind.module.css';
import GoogleCustomLocationField from '@/components/Elements/GoogleCustomLocation';
import {
  CheckBoxField,
  DatePickerField,
  InputField,
  RadioGroupField,
  SelectField,
  TextAreaField,
} from '@/components/Form';
import { currencies } from '@/utils/currenciesUtils';

import {
  ASSET_FIELDS,
  ASSET_KEYS,
  EXPENSE_FIELDS,
  EXPENSE_GROUPS,
  EXPENSE_KEYS,
  ExpenseKey,
  MAX_PREVIOUS_ENTRIES,
  OFTEN_MISSED,
  MLE_HEAD,
  MLE_ROWS,
  YES_NO_OPTIONS,
} from './constant';
import {
  ADDRESS_PARTS,
  EARLIEST_DATE,
  EMPTY_ADDRESS,
  FactFindFormType,
  isExplicitZero,
  historyBlockAction,
  previousEntriesNeeded,
  toAddress,
} from './schema';

type StepProps = {
  control: Control<FactFindFormType>;
  errors: FieldErrors<FactFindFormType>;
  setValue: UseFormSetValue<FactFindFormType>;
};

const ADDRESS_HINT =
  'Start typing and pick your full address (street number, street, suburb, city)';

type AddressPath =
  | 'currentAddress'
  | 'currentEmployerAddress'
  | `previousAddresses.${number}.address`
  | `previousEmployments.${number}.address`;

/**
 * Google address search bound to an address object: the text box is
 * formatted_address, and picking a suggestion fills the other parts.
 */
const AddressField = ({
  name,
  control,
  setValue,
  label,
  required,
  error,
}: Omit<StepProps, 'errors'> & {
  name: AddressPath;
  label: string;
  required?: boolean;
  error?: string;
}) => {
  // The picked suggestion stays as the box text (not Google's longer
  // formattedAddress) so the field keeps showing the picked-address details.
  const pickedRef = useRef('');
  return (
    // Same wrapper as the other form fields, so its label spacing (and the
    // gap below) match the date / text inputs beside it.
    <div className="form-control column">
      <GoogleCustomLocationField
        name={`${name}.formatted_address`}
        control={control}
        label={label}
        subLabel={ADDRESS_HINT}
        size="large"
        required={required}
        location="overseas"
        error={error}
        setPropertyDetailsObject={(details) =>
          setValue(
            name,
            { ...toAddress(details), formatted_address: pickedRef.current },
            { shouldValidate: true, shouldDirty: true },
          )
        }
        onSelectedAddressChange={(picked) => {
          // Typing over a picked address: drop its parts so they can't go stale.
          if (picked === null)
            ADDRESS_PARTS.forEach((k) =>
              setValue(`${name}.${k}` as Path<FactFindFormType>, ''),
            );
          else pickedRef.current = picked;
        }}
      />
    </div>
  );
};

/**
 * Gives every label on the same visual row the height of the tallest one, so
 * side-by-side inputs start on one line (a hint under one label no longer
 * pushes its input down) and an error under an input never moves it.
 * Re-measures on resize and when fields or errors appear.
 */
const useAlignedLabels = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let frame = 0;
    const align = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const labels = Array.from(
          root.querySelectorAll<HTMLElement>('.form-label-wrap'),
        );
        labels.forEach((l) => (l.style.minHeight = ''));
        // Group by the grid row the field sits in (its column's parent row +
        // the column's top), not the label's own pixel position: fields built
        // differently (the Google address field) can start a pixel or two off.
        const rows = new Map<string, HTMLElement[]>();
        const rowIds = new Map<Element, number>();
        labels.forEach((l) => {
          if (!l.offsetParent) return; // hidden (collapsed group)
          const col = l.closest('.ant-col');
          const row = col?.parentElement;
          if (!col || !row) return;
          if (!rowIds.has(row)) rowIds.set(row, rowIds.size);
          const key = `${rowIds.get(row)}:${Math.round(col.getBoundingClientRect().top)}`;
          rows.set(key, [...(rows.get(key) ?? []), l]);
        });
        rows.forEach((group) => {
          if (group.length < 2) return;
          // Labels are content-box with padding; size them by border box.
          group.forEach((l) => (l.style.boxSizing = 'border-box'));
          const h = Math.max(...group.map((l) => l.offsetHeight));
          group.forEach((l) => (l.style.minHeight = `${h}px`));
        });
      });
    };
    align();
    const resize = new ResizeObserver(align);
    resize.observe(root);
    const dom = new MutationObserver(align);
    dom.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      dom.disconnect();
    };
  }, []);
  return ref;
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const alignRef = useAlignedLabels();
  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
      }
      style={{ width: '100%', marginBottom: 20 }}
    >
      <div ref={alignRef} className={styles.fields}>
        {children}
      </div>
    </Card>
  );
};

const SubHeading = ({ children }: { children: ReactNode }) => (
  <Typography.Title level={5} style={{ margin: '8px 0 0' }}>
    {children}
  </Typography.Title>
);

const BLANK_ADDRESS = { address: EMPTY_ADDRESS, startDate: '', endDate: '' };
const BLANK_EMPLOYMENT = {
  occupation: '',
  employer: '',
  address: EMPTY_ADDRESS,
  startDate: '',
  endDate: '',
};

/**
 * Opens the next "previous" block until the history covers HISTORY_YEARS, and
 * closes blocks it opened once they're no longer needed (e.g. the client fixed
 * a start date) - but only while still empty, so typed answers are never lost.
 * Blocks the client added by hand are left alone. Refs keep StrictMode's
 * double effect run from appending/removing twice.
 */
const useHistoryBlocks = <T,>(
  count: number,
  needed: number,
  append: (value: T, options: { shouldFocus: boolean }) => void,
  remove: (index: number) => void,
  blank: T,
  isBlankAt: (index: number) => boolean,
) => {
  const autoOpened = useRef(new Set<number>());
  const lastCount = useRef(-1);
  useEffect(() => {
    // Indexes past the end were removed (by us or "Remove last").
    autoOpened.current.forEach(
      (i) => i >= count && autoOpened.current.delete(i),
    );
    if (lastCount.current === count) return; // already acted for this count
    const last = count - 1;
    const action = historyBlockAction(
      count,
      needed,
      autoOpened.current.has(last) && isBlankAt(last),
    );
    if (action === 'append') {
      lastCount.current = count;
      autoOpened.current.add(count);
      append(blank, { shouldFocus: false });
    } else if (action === 'remove') {
      lastCount.current = count;
      autoOpened.current.delete(last);
      remove(last);
    } else {
      lastCount.current = -1;
    }
  });
};

/** Index of the last field in the two-column pair holding `key` (-1 if absent). */
const pairEnd = (keys: readonly string[], key: string) => {
  const i = keys.indexOf(key);
  return i < 0 ? -1 : Math.min(i - (i % 2) + 1, keys.length - 1);
};

const hasText = (v: unknown) => String(v ?? '').trim() !== '';
const addressText = (a?: { formatted_address?: string | null } | null) =>
  a?.formatted_address;

export const toAmount = (v?: string | null) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

export const formatAmount = (n: number) =>
  n.toLocaleString('en-AU', { maximumFractionDigits: 2 });

export const PersonalStep = ({ control, errors, setValue }: StepProps) => {
  const addresses = useFieldArray({ control, name: 'previousAddresses' });
  const employments = useFieldArray({ control, name: 'previousEmployments' });
  const [addressStart, prevAddresses, jobStart, prevJobs] = useWatch({
    control,
    name: [
      'currentAddressStartDate',
      'previousAddresses',
      'currentEmploymentStartDate',
      'previousEmployments',
    ],
  });
  const addressesNeeded = Math.min(
    previousEntriesNeeded(addressStart, prevAddresses),
    MAX_PREVIOUS_ENTRIES,
  );
  const jobsNeeded = Math.min(
    previousEntriesNeeded(jobStart, prevJobs),
    MAX_PREVIOUS_ENTRIES,
  );
  useHistoryBlocks(
    addresses.fields.length,
    addressesNeeded,
    addresses.append,
    addresses.remove,
    BLANK_ADDRESS,
    (i) => {
      const a = prevAddresses?.[i];
      return ![addressText(a?.address), a?.startDate, a?.endDate].some(hasText);
    },
  );
  useHistoryBlocks(
    employments.fields.length,
    jobsNeeded,
    employments.append,
    employments.remove,
    BLANK_EMPLOYMENT,
    (i) => {
      const e = prevJobs?.[i];
      return ![
        e?.occupation,
        e?.employer,
        addressText(e?.address),
        e?.startDate,
        e?.endDate,
      ].some(hasText);
    },
  );
  const addressHistoryError =
    errors.previousAddresses?.root?.message ??
    errors.previousAddresses?.message;
  const jobHistoryError =
    errors.previousEmployments?.root?.message ??
    errors.previousEmployments?.message;

  return (
    <>
      <Section title="Personal Details">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <InputField
              name="firstName"
              control={control}
              label="Legal First Name"
              subLabel="Must match passport / ID"
              size="large"
              required
              error={errors.firstName?.message}
            />
          </Col>
          <Col xs={24} md={8}>
            <InputField
              name="middleName"
              error={errors.middleName?.message}
              control={control}
              label="Legal Middle Name"
              subLabel="Must match passport / ID"
              size="large"
            />
          </Col>
          <Col xs={24} md={8}>
            <InputField
              name="lastName"
              control={control}
              label="Legal Last Name"
              subLabel="Must match passport / ID"
              size="large"
              required
              error={errors.lastName?.message}
            />
          </Col>
          <Col xs={24}>
            <Typography.Text type="secondary">
              Name prefilled from your profile. You can change it.
            </Typography.Text>
          </Col>
        </Row>
      </Section>

      <Section title="Address History">
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <MessageBox>
              Please list out <strong>all addresses</strong> for the past THREE
              years
            </MessageBox>
          </Col>
          <Col xs={24}>
            <SubHeading>Current Address</SubHeading>
          </Col>
          <Col xs={24} md={16}>
            <AddressField
              name="currentAddress"
              control={control}
              setValue={setValue}
              label="Current Address"
              required
              error={errors.currentAddress?.formatted_address?.message}
            />
          </Col>
          <Col xs={24} md={8}>
            <DatePickerField
              name="currentAddressStartDate"
              control={control}
              label="Start Date"
              size="large"
              required
              dateFormat="DD/MM/YYYY"
              minDate={EARLIEST_DATE}
              maxDate={dayjs()}
              error={errors.currentAddressStartDate?.message}
            />
          </Col>
          {addresses.fields.map((field, i) => (
            <Col xs={24} key={field.id}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <SubHeading>Previous Address {i + 1}</SubHeading>
                  <Typography.Text type="secondary">
                    {i === 0
                      ? 'Please complete if less than 3 years at current address'
                      : 'Please complete if combined period above is less than 3 years'}
                  </Typography.Text>
                </Col>
                <Col xs={24}>
                  <AddressField
                    name={`previousAddresses.${i}.address`}
                    required
                    error={
                      errors.previousAddresses?.[i]?.address?.formatted_address
                        ?.message
                    }
                    control={control}
                    setValue={setValue}
                    label={`Previous Address ${i + 1}`}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    name={`previousAddresses.${i}.startDate`}
                    error={errors.previousAddresses?.[i]?.startDate?.message}
                    control={control}
                    label="Start Date"
                    size="large"
                    required
                    dateFormat="DD/MM/YYYY"
                    minDate={EARLIEST_DATE}
                    maxDate={dayjs()}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    name={`previousAddresses.${i}.endDate`}
                    control={control}
                    label="End Date"
                    size="large"
                    required
                    dateFormat="DD/MM/YYYY"
                    minDate={EARLIEST_DATE}
                    maxDate={dayjs()}
                    error={errors.previousAddresses?.[i]?.endDate?.message}
                  />
                </Col>
              </Row>
            </Col>
          ))}
          <Col xs={24} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {addressHistoryError && (
              <div style={{ flexBasis: '100%' }}>
                <ErrorText error={addressHistoryError} />
              </div>
            )}
            {addresses.fields.length < MAX_PREVIOUS_ENTRIES && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => addresses.append(BLANK_ADDRESS)}
              >
                Add previous address
              </Button>
            )}
            {addresses.fields.length > addressesNeeded && (
              <Button
                icon={<MinusOutlined />}
                onClick={() => addresses.remove(addresses.fields.length - 1)}
              >
                Remove last address
              </Button>
            )}
          </Col>
        </Row>
      </Section>

      <Section title="Employment History">
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <MessageBox>
              <strong>Minimum of THREE years</strong> employment history
              required
            </MessageBox>
          </Col>
          <Col xs={24}>
            <SubHeading>Current Employment</SubHeading>
          </Col>
          <Col xs={24} md={12}>
            <InputField
              name="currentOccupation"
              control={control}
              label="Current Occupation"
              subLabel='If unemployed you may simply write "unemployed"'
              size="large"
              required
              error={errors.currentOccupation?.message}
            />
          </Col>
          <Col xs={24} md={12}>
            <InputField
              name="currentEmployer"
              control={control}
              label="Current Employer"
              subLabel='If unemployed you may simply write "unemployed"'
              size="large"
              required
              error={errors.currentEmployer?.message}
            />
          </Col>
          <Col xs={24} md={16}>
            <AddressField
              name="currentEmployerAddress"
              control={control}
              setValue={setValue}
              label="Address of Current Employer"
              required
              error={errors.currentEmployerAddress?.formatted_address?.message}
            />
          </Col>
          <Col xs={24} md={8}>
            <DatePickerField
              name="currentEmploymentStartDate"
              control={control}
              label="When did you start this role?"
              size="large"
              required
              dateFormat="DD/MM/YYYY"
              minDate={EARLIEST_DATE}
              maxDate={dayjs()}
              error={errors.currentEmploymentStartDate?.message}
            />
          </Col>
          {employments.fields.map((field, i) => (
            <Col xs={24} key={field.id}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <SubHeading>Previous Employment {i + 1}</SubHeading>
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    name={`previousEmployments.${i}.occupation`}
                    required
                    error={errors.previousEmployments?.[i]?.occupation?.message}
                    control={control}
                    label="Previous Occupation"
                    size="large"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    name={`previousEmployments.${i}.employer`}
                    required
                    error={errors.previousEmployments?.[i]?.employer?.message}
                    control={control}
                    label="Previous Employer"
                    size="large"
                  />
                </Col>
                <Col xs={24}>
                  <AddressField
                    name={`previousEmployments.${i}.address`}
                    error={
                      errors.previousEmployments?.[i]?.address
                        ?.formatted_address?.message
                    }
                    control={control}
                    setValue={setValue}
                    label="Address of Previous Employer"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    name={`previousEmployments.${i}.startDate`}
                    error={errors.previousEmployments?.[i]?.startDate?.message}
                    control={control}
                    label="Start Date"
                    size="large"
                    required
                    dateFormat="DD/MM/YYYY"
                    minDate={EARLIEST_DATE}
                    maxDate={dayjs()}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    name={`previousEmployments.${i}.endDate`}
                    error={errors.previousEmployments?.[i]?.endDate?.message}
                    control={control}
                    label="End Date"
                    size="large"
                    dateFormat="DD/MM/YYYY"
                    minDate={EARLIEST_DATE}
                    maxDate={dayjs()}
                  />
                </Col>
              </Row>
            </Col>
          ))}
          <Col xs={24} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {jobHistoryError && (
              <div style={{ flexBasis: '100%' }}>
                <ErrorText error={jobHistoryError} />
              </div>
            )}
            {employments.fields.length < MAX_PREVIOUS_ENTRIES && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => employments.append(BLANK_EMPLOYMENT)}
              >
                Add previous employment
              </Button>
            )}
            {employments.fields.length > jobsNeeded && (
              <Button
                icon={<MinusOutlined />}
                onClick={() =>
                  employments.remove(employments.fields.length - 1)
                }
              >
                Remove last employment
              </Button>
            )}
          </Col>
        </Row>
      </Section>
    </>
  );
};

export const ExpensesStep = ({
  control,
  errors,
  setValue,
  openGroups,
  onOpenGroupsChange,
}: StepProps & {
  // Controlled so the action bar's "Often missed" pills can open a group.
  openGroups: string[];
  onOpenGroupsChange: (keys: string[]) => void;
}) => {
  const currency = useWatch({ control, name: 'expenseCurrency' }) || 'AUD';
  const values = useWatch({ control, name: EXPENSE_KEYS });
  const amounts = Object.fromEntries(
    EXPENSE_KEYS.map((k, i) => [k, toAmount(values[i])]),
  );
  const medical = values[EXPENSE_KEYS.indexOf('medicalHealth')];
  const medicalIsZero = isExplicitZero(medical);
  // The reason box hides when Medical isn't 0; don't keep a stale reason.
  useEffect(() => {
    if (!medicalIsZero) setValue('medicalHealthZeroReason', '');
  }, [medicalIsZero, setValue]);
  const total = Object.values(amounts).reduce((a, b) => a + b, 0);

  return (
    <>
      <Section title="Set Your Currency">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <SelectField
              name="expenseCurrency"
              control={control}
              options={currencies}
              showSearch
              size="large"
              label="Currency"
              required
              error={errors.expenseCurrency?.message}
            />
            <Typography.Text type="secondary">
              All values below will be entered in this currency.
            </Typography.Text>
          </Col>
          <Col xs={24}>
            <MessageBox>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  Lenders will use your spending behaviour to assess your
                  character as a borrower. You may refer to{' '}
                  <strong>APPENDIX 1</strong> below for average living expenses
                  in Australia (excluding rent).
                </li>
                <li>
                  Input monthly living expenses{' '}
                  <strong>
                    assuming your mortgage was successfully approved
                  </strong>
                  . Excessive living expenses will reduce the likelihood of an
                  approval.
                </li>
                <li>
                  Input <strong>HOUSEHOLD living expenses</strong> (excluding
                  rent). Only one applicant per household needs to complete
                  this.
                </li>
                <li>
                  Leave a field blank if there are no expenses (it counts as 0).
                </li>
              </ul>
            </MessageBox>
          </Col>
        </Row>
      </Section>

      <Section title="Monthly Household Living Expense">
        <Typography.Paragraph type="secondary">
          Please estimate your future monthly spending habits to help us assess
          loan affordability.
        </Typography.Paragraph>
        <Collapse
          activeKey={openGroups}
          onChange={(keys) =>
            onOpenGroupsChange(Array.isArray(keys) ? keys : [keys])
          }
          items={EXPENSE_GROUPS.map((g) => ({
            key: g.title,
            label: (
              <span
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>
                  <strong>{g.title}</strong>{' '}
                  <Typography.Text type="secondary">
                    · {g.keys.length} expenses
                  </Typography.Text>
                </span>
                <span>
                  {currency}{' '}
                  {formatAmount(g.keys.reduce((s, k) => s + amounts[k], 0))}
                </span>
              </span>
            ),
            children: (
              <Row gutter={[20, 20]}>
                {g.keys.map((k, i) => (
                  <Fragment key={k}>
                    <Col xs={24} md={12}>
                      <InputField
                        name={k}
                        error={errors[k]?.message}
                        control={control}
                        type="number"
                        allowDecimal
                        prefix={currency}
                        placeholder="0"
                        size="large"
                        label={EXPENSE_FIELDS[k].label}
                        subLabel={EXPENSE_FIELDS[k].help}
                        required={k === 'medicalHealth'}
                      />
                    </Col>
                    {/* Reason goes on its own row after Medical's pair so the
                      paired inputs stay aligned. */}
                    {medicalIsZero &&
                      i === pairEnd(g.keys, 'medicalHealth') && (
                        <Col xs={24}>
                          <TextAreaField
                            name="medicalHealthZeroReason"
                            control={control}
                            label="Reason for 0 Medical and Health expenses"
                            placeholder="E.g. Employer pays for medical costs"
                            rows={3}
                            maxLength={500}
                            required
                            error={errors.medicalHealthZeroReason?.message}
                          />
                        </Col>
                      )}
                  </Fragment>
                ))}
              </Row>
            ),
          }))}
        />
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
            fontWeight: 600,
          }}
        >
          <span>Total monthly household living expenses</span>
          <span>
            {currency} {formatAmount(total)}
          </span>
        </div>
        <MessageBox style={{ marginTop: 16 }}>
          Note: If total living expenses are higher (&gt;10%) than the Average
          Australian Living Expense (see below), the lender will not use the
          minimum living expense threshold when calculating your living
          expenses.
        </MessageBox>
        <Collapse
          style={{ marginTop: 16 }}
          items={[
            {
              key: 'mle',
              label: (
                <span>
                  <strong>APPENDIX 1</strong> · Minimum Living Expenses (MLE)
                  Australian banks use to assess your borrowing power
                </span>
              ),
              children: (
                <>
                  <Typography.Paragraph
                    style={{ textAlign: 'center', margin: 0, fontWeight: 500 }}
                  >
                    Average Australian Living Expense (excluding rent)
                  </Typography.Paragraph>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ textAlign: 'center', margin: '4px 0 0' }}
                  >
                    Per month. Indicative only — lenders update these figures,
                    so your broker will confirm the current numbers.
                  </Typography.Paragraph>
                  <Table
                    style={{ marginTop: 12 }}
                    size="small"
                    bordered
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    rowKey={(r) => r[0]}
                    dataSource={MLE_ROWS}
                    columns={MLE_HEAD.map((title, i) => ({
                      title,
                      key: title,
                      render: (_: unknown, row: string[]) =>
                        i === 0 ? <strong>{row[0]}</strong> : row[i],
                    }))}
                  />
                </>
              ),
            },
          ]}
        />
      </Section>
    </>
  );
};

export const AssetsStep = ({ control, errors }: StepProps) => {
  const changes = useWatch({ control, name: 'foreseeFinancialChanges' });

  return (
    <>
      <Section title="Assets">
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <MessageBox>
              Please enter all asset values in{' '}
              <strong>Australian Dollars (AUD)</strong>, even if your living
              expenses are in another currency.
            </MessageBox>
          </Col>
          {ASSET_KEYS.map((k) => (
            <Col xs={24} md={12} key={k}>
              <InputField
                name={k}
                control={control}
                type="number"
                allowDecimal
                prefix="AUD"
                placeholder="0"
                size="large"
                label={ASSET_FIELDS[k].label}
                subLabel={ASSET_FIELDS[k].help}
                required={ASSET_FIELDS[k].required}
                error={errors[k]?.message}
              />
            </Col>
          ))}
          <Col xs={24}>
            <TextAreaField
              name="otherAssets"
              error={errors.otherAssets?.message}
              control={control}
              label="Other Assets"
              placeholder="E.g. AU$50k share portfolio with CBA"
              rows={3}
              maxLength={500}
            />
            <Typography.Text type="secondary">
              Please list out as many assets as possible. Strong asset positions
              are viewed positively by the bank.
            </Typography.Text>
          </Col>
        </Row>
      </Section>

      <Section title="Financial Circumstances">
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <RadioGroupField
              name="foreseeFinancialChanges"
              control={control}
              label="Do you foresee any significant changes to your financial situation in the near future?"
              options={YES_NO_OPTIONS}
              cols={{ sm: 12, md: 12, lg: 12 }}
              error={errors.foreseeFinancialChanges?.message}
            />
          </Col>
          {changes === 'yes' && (
            <Col xs={24}>
              <TextAreaField
                name="financialChangesDetail"
                control={control}
                label="Please provide a detailed answer"
                subLabel='Please note that if your answer is "Yes", there is a chance the Bank will not approve your application.'
                placeholder="Describe the change and when you expect it"
                rows={4}
                maxLength={500}
                required
                error={errors.financialChangesDetail?.message}
              />
            </Col>
          )}
          <Col xs={24} md={12}>
            <InputField
              name="retirementAge"
              control={control}
              type="number"
              maxLength={3}
              size="large"
              label="At what age do you plan to retire?"
              subLabel="Working past the standard retirement age is generally viewed favourably by lenders"
              required
              error={errors.retirementAge?.message}
            />
          </Col>
          <Col xs={24}>
            <div
              style={{
                padding: 16,
                borderRadius: 8,
                background: '#f3f4f6',
                border: `1px solid ${errors.declaration ? '#dc2626' : '#f3f4f6'}`,
              }}
            >
              <CheckBoxField
                name="declaration"
                control={control}
                label="By submitting this form, you declare that the information provided in this Fact Find and your Online Application is accurate and true, and that you understand that any wilful dishonesty may result in refusal of this application."
                error={errors.declaration?.message}
              />
            </div>
          </Col>
        </Row>
      </Section>
    </>
  );
};

/** Step 2 action bar row: the six often-missed expenses as jump-to pills. */
export const OftenMissed = ({
  filled,
  onPick,
}: {
  filled: Record<string, boolean>;
  onPick: (key: ExpenseKey) => void;
}) => {
  const done = OFTEN_MISSED.filter((m) => filled[m.key]).length;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        paddingBottom: 12,
        marginBottom: 12,
        borderBottom: '1px solid #e1e1e1',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 500 }}>Often missed:</span>
      {OFTEN_MISSED.map((m) => {
        const isDone = filled[m.key];
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onPick(m.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              minHeight: 30,
              padding: '0 10px',
              borderRadius: 999,
              font: 'inherit',
              cursor: 'pointer',
              border: isDone ? '1px solid #bbf7d0' : '1px dashed #cbd5e1',
              background: isDone ? '#f0fdf4' : '#ffffff',
              color: isDone ? '#166534' : '#475569',
            }}
          >
            {isDone ? (
              <CheckOutlined aria-hidden />
            ) : (
              <PlusOutlined aria-hidden />
            )}
            {m.label}
          </button>
        );
      })}
      <span style={{ marginLeft: 'auto', color: '#64748b' }}>
        {done} of {OFTEN_MISSED.length} filled
      </span>
    </div>
  );
};

/** Shown on Next when the total is under the household benchmark. */
export const LowTotalAlert = ({ onConfirm }: { onConfirm: () => void }) => (
  <div
    role="alert"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
      padding: '10px 14px',
      marginBottom: 12,
      borderRadius: 8,
      border: '1px solid #fcd34d',
      background: '#fffbeb',
      fontSize: 14,
    }}
  >
    <InfoCircleOutlined aria-hidden style={{ color: '#b45309' }} />
    <span style={{ flex: '1 1 260px' }}>
      Your total looks lower than similar households usually spend. Tap any
      blank item above to add it, or confirm your figures.
    </span>
    <Button onClick={onConfirm}>My figures are correct</Button>
  </div>
);
